/**
 * Connector status, as one customer's own Integrations screen projects it,
 * plus failure counts. Pure: it renders an outcome it is handed. Used by the
 * Integrations board (every customer) and a customer's detail page.
 */
import type { ConsoleOutcome } from '../platform/client.js';
import type { CustomerConnector, CustomerIntegrationsRead } from '../platform/tenancy.js';

export type IntegrationsOutcome = ConsoleOutcome<CustomerIntegrationsRead> | null | undefined;

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never');

function failures(c: CustomerConnector): string {
  const { total, byCode } = c.failuresInWindow;
  if (total === 0) return '0';
  return `${total} (${Object.entries(byCode)
    .map(([code, n]) => `${code.replace(/_/g, ' ')} ${n}`)
    .join(', ')})`;
}

export function ConnectorRows({
  customer,
  connectors,
}: {
  customer?: string;
  connectors: readonly CustomerConnector[];
}) {
  return (
    <>
      {connectors.map((c) => (
        <tr key={`${customer ?? ''}${c.kind}`} data-attention={c.needsAttention ? 'yes' : 'no'}>
          {customer === undefined ? null : <td data-label="Customer">{customer}</td>}
          <td data-label="Connector">{c.kind}</td>
          <td data-label="Status">
            {c.needsAttention ? <strong>{c.statusLabel}</strong> : c.statusLabel}
            {c.syncRunning ? <span className="quiet"> · importing now</span> : null}
            {c.enabled ? null : <span className="quiet"> · disabled</span>}
          </td>
          <td data-label="Last successful sync">
            {when(c.lastSyncedAt)}
            <span className="quiet"> · {c.staleness}</span>
          </td>
          <td data-label="History">
            {c.historicalSyncComplete ? 'complete' : c.historicalSyncPhase}
          </td>
          <td data-label="Failed runs (7d)">{failures(c)}</td>
          <td data-label="Quarantined (7d)">{c.quarantinedRecordsInWindow}</td>
          <td data-label="Open alerts">{c.openIssues ? c.openIssues.total : '—'}</td>
        </tr>
      ))}
    </>
  );
}

export function ConnectorHead({ withCustomer }: { withCustomer: boolean }) {
  return (
    <thead>
      <tr>
        {withCustomer ? <th>Customer</th> : null}
        <th>Connector</th>
        <th>Status</th>
        <th>Last successful sync</th>
        <th>History</th>
        <th>Failed runs (7d)</th>
        <th>Quarantined (7d)</th>
        <th>Open alerts</th>
      </tr>
    </thead>
  );
}

/** A customer's detail page: the served read, rendered, or why it is not. */
export function IntegrationsSection({ outcome }: { outcome: IntegrationsOutcome }) {
  return (
    <section>
      <h3>Integrations</h3>
      {!outcome ? (
        <p className="quiet">Reading connector status…</p>
      ) : outcome.kind !== 'ok' ? (
        <div className="banner" role="alert">
          <strong>Connector status did not answer.</strong> {outcome.message}
        </div>
      ) : outcome.value.connectors.length === 0 ? (
        <p>No connector is set up for this customer (a measured zero).</p>
      ) : (
        <table className="stack">
          <ConnectorHead withCustomer={false} />
          <tbody>
            <ConnectorRows connectors={outcome.value.connectors} />
          </tbody>
        </table>
      )}
    </section>
  );
}
